
import { defineComponent } from "vue";
import { storeToRefs } from "pinia";
import ThresholdFilterOptions from "./thresholdFilterOptions.vue";
import { useActiveStore } from "@/stores/ssaStore/active";
import { useDisplayPaneStore } from "@/stores/ssaStore/displayPane";
import { ParaviewFilterPlus } from "@/types/dataset";
import { usePvwStore } from "@/stores/ssaStore/pvw";
import { useFiltersStore } from "@/stores/ssaStore/filters";
import { loadSaveFilterIdPrefix } from "@/utils/DisplayPaneConstants";
export default defineComponent({
	name: "imdex-threshold-filter-manager",
	components: { ThresholdFilterOptions },
	setup() {
		const activeStore = useActiveStore();
		const pvwStore = usePvwStore();
		const { displayStates } = storeToRefs(useDisplayPaneStore());
		const { filtersIconClickedCount } = storeToRefs(useFiltersStore());
		const filtersStore = useFiltersStore();
		return { activeStore, pvwStore, displayStates, filtersStore, filtersIconClickedCount };
	},
	props: {
		activateLayer: {
			type: Function,
			required: true
		}
	},
	data() {
		return {
			thresholdFilterOptionsOpen: false,
			outsideClickListener: null as any,
			initialLayerValue: "",
			initialPropValue: ""
		};
	},
	computed: {
		allActiveFilters(): ParaviewFilterPlus[] {
			return (
				this.filtersStore.SSA_ALL_ACTIVE_FILTERS.filter(
					(filter: ParaviewFilterPlus) => !filter.hideMetadataOnlyFilter
				) ?? []
			);
		},
		activeLayersForDataset() {
			return this.displayStates[this.activeStore.SSA_ACTIVE_VIEW]?.[
				this.activeStore.SSA_ACTIVE_DATASET.id
			];
		}
	},
	methods: {
		addNewFilter() {
			this.thresholdFilterOptionsOpen = false;
			this.initialLayerValue = "";
			this.initialPropValue = "";
			this.openThresholdFilterOptions();
			return;
		},
		showFilterOptions(filter: ParaviewFilterPlus) {
			this.thresholdFilterOptionsOpen = false;
			if (filter.layerName != this.activeStore.SSA_ACTIVE_LAYER)
				this.activateLayer(filter.layerName, true);
			this.initialLayerValue = filter.layerName;
			this.initialPropValue = filter.Scalars[1].split("__DATETIME__")[0];
			this.openThresholdFilterOptions();
			return;
		},
		hideFilter(filter: ParaviewFilterPlus) {
			if (filter.layerName != this.activeStore.SSA_ACTIVE_LAYER)
				this.activateLayer(filter.layerName, true);
			this.filtersStore.SSA_HIDE_FILTER_SHOW_SLASH(filter);

			return;
		},
		deleteFilter(filter: ParaviewFilterPlus) {
			if (filter.layerName != this.activeStore.SSA_ACTIVE_LAYER)
				this.activateLayer(filter.layerName, true);

			if (filter.disableButDisplayFilterSlashEye)
				this.filtersStore.SSA_HIDE_FILTER_SHOW_SLASH(filter);

			if (filter.id.startsWith(loadSaveFilterIdPrefix)) {
				// the clicked filter, if clicked right after save state when the layer isn't active,
				// will be replaced by a loaded filter when the layer becomes active; as the filter
				// that was clicked no longer exists, we need to find the replacing filter's id
				// to remove instead of the placeholder id
				const replacingFilter = this.allActiveFilters.find(
					(updatedFilter: ParaviewFilterPlus) =>
						filter.Scalars[1].split("__DATETIME__")[0] ===
							updatedFilter.Scalars[1].split("__DATETIME__")[0] &&
						filter.layerName === updatedFilter.layerName
				);
				if (replacingFilter) filter = replacingFilter;
				else {
					// this shouldn't occur, and if it does, the filter may already be removed
					console.error(
						`🚀🤗 ~ deleteFilter ~ replacingFilter not found for filter:`,
						filter
					);
				}
			}

			this.pvwStore.PVW_FILTER_THRESHOLD_REMOVE({
				layerName: filter.layerName,
				viewId: this.activeStore.view,
				filterId: filter.id
			});

			return;
		},
		openThresholdFilterOptions() {
			this.thresholdFilterOptionsOpen = !this.thresholdFilterOptionsOpen;

			const container: any = window.document.getElementById("pane-container");
			if (this.thresholdFilterOptionsOpen) {
				this.$nextTick(() => {
					window.addEventListener(
						"mousedown",
						(this.outsideClickListener = (e: any) => {
							const [x, y] = [e.pageX, e.pageY];
							const elements = document.elementsFromPoint(x, y);
							for (let i = 0; i < elements.length; i++) {
								if (elements[i].classList.contains("threshold-filter-options")) {
									return;
								}
								if (elements[i].classList.contains("d-select-menu")) {
									return;
								}
							}
							this.thresholdFilterOptionsOpen = false;
							window.removeEventListener("mousedown", this.outsideClickListener);
						})
					);

					const tFOptionsAnchor = this.$refs.tFOptionsAnchor as any;
					const { left, top, width } = tFOptionsAnchor.getBoundingClientRect();

					const tFOptions = this.$refs.thresholdFilterOptions as any;
					const { height: elHeight } = tFOptions.$el.getBoundingClientRect();

					if (left - 350 < 0) {
						tFOptions.$el.style.left = left + width + "px";
					} else {
						tFOptions.$el.style.left = left - 350 + "px";
					}

					if (top + elHeight > window.innerHeight) {
						tFOptions.$el.style.top = window.innerHeight - elHeight - 15 + "px";
					} else {
						tFOptions.$el.style.top = top + "px";
					}

					container.appendChild(tFOptions.$el as any);
				});
			}
		},
		layerSelected(filter: ParaviewFilterPlus) {
			return this.activeLayersForDataset?.find(
				(layer: any) => layer.layerName == filter.layerName && layer.selected
			);
		}
	},
	watch: {
		filtersIconClickedCount: {
			handler() {
				this.initialPropValue = "";
				this.initialLayerValue = "";
				if (
					this.allActiveFilters.length === 1 &&
					this.filtersStore.filtersIconClickedLayerName === ""
				) {
					if (this.allActiveFilters[0].layerName != this.activeStore.SSA_ACTIVE_LAYER)
						this.activateLayer(this.allActiveFilters[0].layerName, true);
					// if only one filter exists, and we don't have a desginated layer, assign it
					this.initialLayerValue = this.allActiveFilters[0].layerName;
					this.initialPropValue =
						this.allActiveFilters[0].Scalars[1].split("__DATETIME__")[0];
				} else if (this.filtersStore.filtersIconClickedLayerName) {
					// if we have a designated layer, assign it. If one filter exists for that layer, default to it as well.
					this.initialLayerValue = this.filtersStore.filtersIconClickedLayerName;
					const activeFiltersForLayer = this.allActiveFilters.filter(
						(filter: ParaviewFilterPlus) =>
							filter.layerName == this.filtersStore.filtersIconClickedLayerName
					);

					if (activeFiltersForLayer && activeFiltersForLayer.length === 1) {
						this.initialPropValue =
							activeFiltersForLayer[0].Scalars[1].split("__DATETIME__")[0];
					}
				}

				if (!this.thresholdFilterOptionsOpen) {
					this.openThresholdFilterOptions();
				}
				return;
			}
		}
	}
});
